import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FC } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Radio,
  Stack,
  ToggleButton,
  Typography,
} from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useAccount } from "@tlc/ls2pac/components/Account";
import {
  AllListsSummary,
  ListService,
  ListType,
} from "../../account/ListService";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { FormTextField } from "@tlc/ls2pac/components/FormTextField";
import { HigherContrastToggleButtonGroup } from "@tlc/ls2pac/components/ResultsViewToggle";
import { ChildCheckboxGroup } from "../../account/lists/ChildSelectionDialog";
import { useToast } from "../../../../contexts/ToastContext";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useSupportedFunctionality } from "@tlc/ls2pac/components/SupportedFunctionality";
import { BorrowerChild } from "@tlc/ls2pac/dto/BorrowerChild";

const ADD_TO_LIST_DIALOG_WIDTH = "440px";

export function useListTypeLabel(): Record<ListType, string> {
  const { translate } = useLanguage();
  return {
    [ListType.Prohibited]: translate("prohibited"),
    [ListType.Config]: translate("config"),
    [ListType.Personal]: translate("personal"),
  };
}

export function useAddToListResponseHandler() {
  const { translate } = useLanguage();
  const { showToast } = useToast();

  return ({
    totalEResourcesNotAdded,
    successCount,
  }: {
    success: boolean;
    totalEResourcesNotAdded: number;
    successCount: number;
  }) => {
    // Prohibited lists don't support eResources
    if (totalEResourcesNotAdded > 0 && successCount === 0) {
      showToast(
        `${translate("addToProhibitedListError", {
          ERROR_COUNT: totalEResourcesNotAdded.toString(),
        })} ${translate("addToProhibitedListEResourceError")}`,
        "error"
      );
    }

    if (totalEResourcesNotAdded > 0 && successCount > 0) {
      showToast(
        <>
          {translate("addToProhibitedListSuccess", {
            SUCCESS_COUNT: successCount.toString(),
          })}
          <br />
          <br />
          {translate("addToProhibitedListError", {
            ERROR_COUNT: totalEResourcesNotAdded.toString(),
          })}{" "}
          {translate("addToProhibitedListEResourceError")}
        </>,
        "info"
      );
    }

    if (successCount > 0 && totalEResourcesNotAdded === 0) {
      // display a success toast
      showToast(
        translate("addToProhibitedListSuccess", {
          SUCCESS_COUNT: successCount.toString(),
        }),
        "success"
      );
    }
    if (successCount === 0 && totalEResourcesNotAdded === 0) {
      showToast(translate("addToListErrorMessage"), "error");
    }
  };
}

interface CreateListFormInput {
  listName: string;
  type: ListType;
  childIds: BorrowerChild["id"][];
}
interface CreateListFormProps {
  resourceIds: number[];
  defaultName?: string;
  onClickBack: () => void;
  onClose: () => void;
}
export const CreateListForm: React.FC<CreateListFormProps> = ({
  defaultName,
  resourceIds,
  onClickBack,
  onClose,
}) => {
  const { supportedFunctionality } = useSupportedFunctionality();
  const { useGuardianProhibition } = supportedFunctionality || {};
  const { translate } = useLanguage();
  const listTypeLabel = useListTypeLabel();
  const isMobile = useIsMobile();
  const handleResponse = useAddToListResponseHandler();
  const { patron, isConfigAdmin, isGuardian, isAuthenticated } = useAccount();
  const defaultType = useMemo(() => {
    if (isGuardian && useGuardianProhibition) {
      return ListType.Prohibited;
    }
    if (isConfigAdmin) {
      return ListType.Config;
    }
    return ListType.Personal;
  }, [isConfigAdmin, isGuardian, useGuardianProhibition]);

  const listTypes = useMemo(() => {
    return Object.values(ListType).filter(
      (lt) =>
        lt === ListType.Personal ||
        (lt === ListType.Config && isConfigAdmin) ||
        (lt === ListType.Prohibited && isGuardian && useGuardianProhibition)
    );
  }, [isConfigAdmin, isGuardian, useGuardianProhibition]);

  const formMethods = useForm<CreateListFormInput>({
    mode: "onBlur",
    defaultValues: {
      listName: defaultName || null,
      type: defaultType,
      childIds: [],
    },
  });

  const { handleSubmit, setValue } = formMethods;
  const type = useWatch({ name: "type", control: formMethods.control });

  const onSubmit = async ({
    listName,
    type,
    childIds,
  }: CreateListFormInput) => {
    const response = await ListService.createList(
      listName,
      type,
      resourceIds,
      childIds
    );
    handleResponse(response);
    onClose();
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <IconButton onClick={() => onClickBack()}>
            <ArrowBackIosNewIcon />
          </IconButton>{" "}
          {translate("createNewList")}
        </DialogTitle>
        <DialogContent
          sx={{ width: isMobile ? "auto" : ADD_TO_LIST_DIALOG_WIDTH }}
        >
          <Stack paddingTop={2}>
            <FormTextField<CreateListFormInput>
              required
              name={"listName"}
              label={"List Name"}
              maxLength={255}
            />
            {listTypes.length > 1 && (
              <Controller<CreateListFormInput, "type">
                render={({ field, fieldState, formState }) => {
                  return (
                    <HigherContrastToggleButtonGroup
                      color="secondary"
                      exclusive
                      size="small"
                      aria-label="List Type"
                      value={field.value}
                      onChange={(
                        _: React.MouseEvent<HTMLElement>,
                        newValue: ListType | null
                      ) => {
                        // don't allow deselection
                        if (newValue != null) {
                          setValue("childIds", []); // reset childIds when changing tabs
                          field.onChange(newValue);
                        }
                      }}
                    >
                      {listTypes.map((lt) => {
                        return (
                          <ToggleButton
                            key={lt}
                            value={lt}
                            sx={{ pr: "12px", pl: "12px" }}
                          >
                            <Stack
                              direction="row"
                              spacing={{ xs: 1 }}
                              useFlexGap
                            >
                              {field.value === lt && <CheckIcon />}
                              {listTypeLabel[lt]}
                            </Stack>
                          </ToggleButton>
                        );
                      })}
                    </HigherContrastToggleButtonGroup>
                  );
                }}
                name={"type"}
              />
            )}
            <Collapse
              in={type === ListType.Prohibited && !!patron?.children}
              sx={{ mt: 2 }}
            >
              <ChildCheckboxGroup<CreateListFormInput>
                label={translate("associatedStudentsDirections")}
                name="childIds"
                students={patron?.children}
              />
            </Collapse>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()}>{translate("cancel")}</Button>
          <Button variant="contained" type="submit">
            {translate("done")}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

interface AddToListDialogProps {
  open: boolean;
  onClose: () => void;
  resourceIds: number[];
  defaultName: string;
}

export const AddToListDialog: FC<AddToListDialogProps> = ({
  open,
  onClose,
  resourceIds,
  defaultName,
}) => {
  const { supportedFunctionality } = useSupportedFunctionality();
  const { useGuardianProhibition } = supportedFunctionality || {};
  const { translate } = useLanguage();
  const handleResponse = useAddToListResponseHandler();
  const { isConfigAdmin, isGuardian, isAuthenticated } = useAccount();
  const isMobile = useIsMobile();
  const listTypeLabel = useListTypeLabel();

  const [allLists, setAllLists] = useState<AllListsSummary[] | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<ListType[]>([]);
  const [selectedList, setSelectedList] = useState<AllListsSummary | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);

  const [creating, setCreating] = useState<boolean>(false);
  useEffect(() => {
    // reset setCreating on open
    if (open) setCreating(false);
  }, [open]);

  const listTypes = useMemo(() => {
    if (isGuardian && useGuardianProhibition && isConfigAdmin)
      return [ListType.Prohibited, ListType.Config, ListType.Personal];
    if (isGuardian && useGuardianProhibition)
      return [ListType.Prohibited, ListType.Personal];
    if (isConfigAdmin) return [ListType.Config, ListType.Personal];
    return [ListType.Personal];
  }, [isConfigAdmin, isGuardian, useGuardianProhibition]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAllLists(null);
      onClose();
      return;
    }
    if (!open) {
      return;
    }

    setLoading(true);
    ListService.getAllLists().then(({ allLists }) => {
      setAllLists(allLists);
      if (allLists.length > 0) {
        setSelectedList(allLists[0]);
      }
      setLoading(false);
    });
  }, [open, isAuthenticated]);

  const filteredListGroups = useMemo(() => {
    if (!allLists) return [];
    return listTypes
      .filter(
        (type) => appliedFilters.length === 0 || appliedFilters.includes(type)
      )
      .map((type) => ({
        type,
        items: allLists.filter((li) => li.type === type),
      }))
      .filter(({ items }) => items.length > 0);
  }, [allLists, listTypes, appliedFilters]);

  if (!open || !isAuthenticated) {
    return null;
  }

  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      onClose={() => {
        onClose();
      }}
    >
      {creating ? (
        <CreateListForm
          onClickBack={() => setCreating(false)}
          onClose={onClose}
          resourceIds={resourceIds}
          defaultName={defaultName}
        />
      ) : (
        <>
          <DialogTitle>{translate("addToList")}</DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: isMobile ? "auto" : ADD_TO_LIST_DIALOG_WIDTH,
            }}
          >
            <Button
              loading={loading}
              variant="outlined"
              fullWidth
              onClick={() => setCreating(true)}
            >
              {translate("createNewList")}
            </Button>
            <Typography>{translate("addToListControl_header")}</Typography>
            {!loading && listTypes.length > 1 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap" }}
                useFlexGap
              >
                <Chip
                  sx={{ textTransform: "capitalize" }}
                  label={`${translate("all")} (${(allLists || []).length})`}
                  onClick={() => {
                    setAppliedFilters([]);
                  }}
                  variant="outlined"
                  color={appliedFilters.length === 0 ? "secondary" : "default"}
                  icon={appliedFilters.length === 0 ? <CheckIcon /> : <></>}
                />
                {listTypes.map((lt) => {
                  const label = listTypeLabel[lt];
                  const listCount = (allLists || []).filter(
                    (l) => l.type === lt
                  ).length;
                  const selected = appliedFilters.includes(lt);
                  return (
                    <Chip
                      key={lt}
                      onClick={() => {
                        setAppliedFilters((prev) => {
                          if (prev.includes(lt)) {
                            return prev.filter((f) => f !== lt);
                          }
                          return [...prev, lt];
                        });
                      }}
                      variant="outlined"
                      label={`${label} (${listCount})`}
                      disabled={listCount === 0}
                      color={selected ? "secondary" : "default"}
                      icon={selected ? <CheckIcon /> : <></>}
                    />
                  );
                })}
              </Stack>
            )}
            <List disablePadding>
              {!loading &&
                filteredListGroups.map(({ type, items }) => {
                  return (
                    <React.Fragment key={type}>
                      {listTypes.length > 1 && (
                        <ListSubheader>{listTypeLabel[type]}</ListSubheader>
                      )}
                      {items.map((li) => {
                        const selected = li.id === selectedList?.id;
                        const count = li.count || 0; // count can be null for default list
                        return (
                          <ListItemButton
                            key={li.id}
                            selected={selected}
                            onClick={() => setSelectedList(li)}
                          >
                            {/* count can be null for default list */}
                            <ListItemText
                              primary={li.name}
                              secondary={`${count} ${translate(
                                count === 1 ? "Resource" : "resources"
                              )}`}
                            ></ListItemText>
                            <ListItemIcon>
                              <Radio
                                edge="start"
                                checked={selected}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                          </ListItemButton>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose()}>{translate("cancel")}</Button>
            <Button
              variant="contained"
              onClick={async () => {
                // !selectedList.id is the default list. we really need to deprecate this behavior
                const response = !selectedList?.id
                  ? await ListService.createList(
                      selectedList.name,
                      selectedList.type,
                      resourceIds
                    )
                  : await ListService.addToList(
                      selectedList.id,
                      selectedList.type,
                      resourceIds
                    );

                handleResponse(response);
                onClose();
              }}
            >
              {translate("done")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

interface AddToListDialogContextType {
  showDialog: (resourceIds: number[], defaultName?: string) => void;
}

const addToListDialogContext = createContext<
  AddToListDialogContextType | undefined
>(undefined);

export const useAddToListDialog = (): AddToListDialogContextType => {
  const context = useContext(addToListDialogContext);
  if (!context)
    throw new Error(
      "useAddToListDialog must be used within a AddToListDialogProvider"
    );
  return context;
};

export const AddToListDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [resourceIds, setResourceIds] = useState<number[] | null>();
  const [defaultName, setDefaultName] = useState<string | null>();
  const [open, setOpen] = useState<boolean>(false);

  //   TODO: return a Promise with the result of the dialog?
  const showDialog = useCallback(
    (resourceIds: number[], defaultName?: string) => {
      if (!resourceIds || !Array.isArray(resourceIds) || !resourceIds.length) {
        console.warn(
          "AddToListDialogProvider.showDialog called without resourceIds"
        );
        return;
      }
      setDefaultName(defaultName || null);
      setResourceIds(resourceIds);
      // TODO: replace promptIfNotLoggedIn this with a context/provider
      LoginClientManager.promptIfNotLoggedIn(() => {
        setOpen(true);
      });
    },
    []
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <addToListDialogContext.Provider value={{ showDialog }}>
      <AddToListDialog
        resourceIds={resourceIds}
        open={open}
        onClose={handleClose}
        defaultName={defaultName}
      />
      {children}
    </addToListDialogContext.Provider>
  );
};
